import { FunctionComponent, useMemo } from 'react';
import Slider from '@mui/material/Slider';

const formatearMesEsp = (yyyymm: string): string => {
  const match = yyyymm.match(/^(\d{4})-(\d{2})$/);
  if (!match) return yyyymm;
  const meses = [
    'ene',
    'feb',
    'mar',
    'abr',
    'may',
    'jun',
    'jul',
    'ago',
    'sep',
    'oct',
    'nov',
    'dic'
  ];
  const mIdx = parseInt(match[2], 10) - 1;
  if (mIdx < 0 || mIdx > 11) return yyyymm;
  return `${meses[mIdx]} ${match[1]}`;
};

const RangoTemporalSlider: FunctionComponent<{
  meses: string[];
  desde: string;
  hasta: string;
  onChange: (desde: string, hasta: string) => void;
}> = ({ meses, desde, hasta, onChange }) => {
  const desdeIdx = useMemo(() => Math.max(0, meses.indexOf(desde)), [meses, desde]);
  const hastaIdx = useMemo(() => {
    const idx = meses.indexOf(hasta);
    return idx === -1 ? meses.length - 1 : idx;
  }, [meses, hasta]);

  if (meses.length < 2) {
    return null;
  }

  const handleChange = (_: Event, value: number | number[]) => {
    if (!Array.isArray(value) || value.length < 2) return;
    const [d, h] = value;
    if (d === desdeIdx && h === hastaIdx) return;
    const dSafe = Math.max(0, Math.min(meses.length - 1, d));
    const hSafe = Math.max(0, Math.min(meses.length - 1, h));
    if (dSafe >= hSafe) return;
    onChange(meses[dSafe], meses[hSafe]);
  };

  const tickCount = Math.min(6, meses.length);
  const step = Math.max(1, Math.floor(meses.length / Math.max(1, tickCount - 1)));
  const marks = Array.from({ length: tickCount }, (_, i) => {
    const idx = Math.min(meses.length - 1, i * step);
    return { value: idx, label: formatearMesEsp(meses[idx]) };
  });

  return (
    <div className='rango-temporal-slider'>
      <div className='rango-temporal-slider-header'>
        <span className='rango-temporal-slider-label'>
          Desde <strong>{formatearMesEsp(meses[desdeIdx])}</strong>
        </span>
        <span className='rango-temporal-slider-label'>
          Hasta <strong>{formatearMesEsp(meses[hastaIdx])}</strong>
        </span>
      </div>
      <Slider
        value={[desdeIdx, hastaIdx]}
        onChange={handleChange}
        min={0}
        max={meses.length - 1}
        step={1}
        marks={marks}
        valueLabelDisplay='auto'
        valueLabelFormat={(v: number) => formatearMesEsp(meses[v] ?? '')}
        disableSwap
        sx={{
          color: 'var(--vdr-cyan-dark)',
          height: 4,
          '& .MuiSlider-rail': { backgroundColor: 'var(--vdr-line-strong)', opacity: 1 },
          '& .MuiSlider-track': { border: 'none' },
          '& .MuiSlider-thumb': {
            width: 20,
            height: 20,
            boxSizing: 'border-box',
            borderRadius: 0,
            backgroundColor: 'var(--vdr-white)',
            border: '3px solid var(--vdr-ink)',
            boxShadow: 'none',
            '&::before': { boxShadow: 'none' },
            '&:hover, &.Mui-focusVisible': { boxShadow: 'var(--vdr-focus-ring)' },
            '&.Mui-active': { boxShadow: 'var(--vdr-focus-ring)' }
          },
          '& .MuiSlider-mark': {
            width: '1px',
            height: '10px',
            backgroundColor: 'var(--vdr-line-strong)'
          },
          '& .MuiSlider-markActive': { backgroundColor: 'var(--vdr-cyan-dark)', opacity: 1 },
          '& .MuiSlider-markLabel': {
            fontFamily: 'var(--vdr-font-mono)',
            fontSize: 11,
            letterSpacing: '0.04em',
            color: 'var(--vdr-mute)',
            top: 28
          },
          '& .MuiSlider-valueLabel': {
            backgroundColor: 'var(--vdr-ink)',
            borderRadius: 0,
            fontFamily: 'var(--vdr-font-mono)',
            fontSize: 11.5,
            letterSpacing: '0.04em',
            padding: '4px 8px'
          }
        }}
      />
    </div>
  );
};

export default RangoTemporalSlider;
